import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { cn } from 'src/utils/shadcn';
import { PayStation } from 'src/features/payment/utils/types';
import { payStationFormSchema, PayStationFormSchemaValues } from 'src/features/payment/utils/formSchema';
import { useGetPayStation } from 'src/features/payment/api/useGetPayStation';
import PayStationForm from 'src/features/payment/components/forms/PayStationForm';
import { useListSites } from 'src/features/sites/api/useListSites';
import { useParking } from 'src/providers/ParkingProvider';

interface PayStationFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPayStation?: PayStation;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: PayStationFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function PayStationFormModal<TData>({
    selectedPayStation,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: PayStationFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const { parkingId } = useParking();
    const { data: payStation } = useGetPayStation({
        parkingId,
        payStationId: selectedPayStation?.id ?? '',
        // We only want to fetch if we're in edit
        config: { enabled: !!selectedPayStation?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(
        () =>
            payStation
                ? { ...payStation, site: payStation.site.id, gateway: payStation.gateway?.id }
                : {
                      name: '',
                      code: '',
                      site: undefined,
                      settings: {
                          coin_selector: {
                              accepted_nominals: [],
                              payout_nominals: [],
                          },
                          banknote_validator: {
                              accepted_nominals: [],
                              payout_nominals: [],
                          },
                          pipeline_profile: undefined,
                      },
                      ui_mode: undefined,
                      backend_url: undefined,
                      backend_api_key: undefined,
                      gateway: undefined,
                  },
        [payStation],
    );

    const form = useForm<PayStationFormSchemaValues>({
        resolver: zodResolver(payStationFormSchema),
        defaultValues,
    });

    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        form.reset(defaultValues);
    }, [payStation, isModalOpen, form, defaultValues]);

    const { data: sitesData } = useListSites({ parkingId });

    const filteredEmptySitesTranslations = React.useMemo(() => {
        return sitesData?.results?.filter((site) => site.translations.length > 0) ?? [];
    }, [sitesData?.results]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: PayStationFormSchemaValues) =>
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            ),
        [mutate, form, onError],
    );
    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <PayStationForm form={form} onSubmit={onSubmit} siteOptions={filteredEmptySitesTranslations} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedPayStation && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default PayStationFormModal;
